<template>
  <div>
    <Menu />
    <div class="container main">
      <div class="row">
        <div class="col-md-12">
          <SectionTitle
            title="Consulta de Cidadãos"
            description="Busque por nome, CPF e visualize os dados do e-SUS."
            url="/gestao-cadastros"
            :enabledModalInformacao="true"
          />
        </div>
        <div class="col-md-12">
          <ConsultaCidadaos
            :modal-id="modalId"
            :auto-open="false"
            :resultados="resultados"
            :busy="loading"
            :loading-id="loadingCidKey"
            @buscar="buscarCidadao"
            @limpar="limparFiltros"
            @exportar="exportarResultados"
            @visualizar="visualizarCidadao"
            @invalid="
              (msg) => $toast.error(msg, 'Error:', { position: 'topRight' })
            "
          />
        </div>
      </div>
    </div>
    <Footer />

    <ModalCidadao :cidadao="pessoa" :cnsList="cnsList" />
  </div>
</template>

<script>
import Menu from '@/components/Menu'
import Footer from '@/components/Footer'
import SectionTitle from '@/components/SectionTitle'
import ModalCidadao from './components/ModalCidadao.vue'
import ConsultaCidadaos from './components/ConsultaCidadaos.vue'
import utils from '@/mixins/utils.js'

export default {
  name: 'Cidadao',
  metaInfo: {
    title: 'Consulta de Cidadãos',
    titleTemplate: null,
  },
  components: {
    Menu,
    Footer,
    SectionTitle,
    ModalCidadao,
    ConsultaCidadaos,
  },
  mixins: [utils],
  data() {
    return {
      modalId: 'consulta-cidadaos-modal',
      loading: false,
      resultados: [],
      pessoa: null,
      activeKey: 'historico',
      loadingCidKey: null,
      cnsList: [],
    }
  },
  computed: {
    fonteSincronizadorC() {
      if (this.$store.getters['getSincronizadorC'] !== null) {
        return this.$store.getters['getSincronizadorC']
      }
      return null
    },
  },
  mounted() {
    this.$bvModal.show(this.modalId)
  },
  methods: {
    /**
     * Busca cidadãos com base nos critérios informados.
     * @param {Object} filtros Critérios de busca (cpf, cns, nome, dataNascimento)
     */
    buscarCidadao({ opcoes, valor, dataNascimento }) {
      this.loading = true

      const params = {}

      // monta params dinamicamente
      if (opcoes.cpf) {
        params.cpf = valor.replace(/\D/g, '')
      }

      if (opcoes.cns) {
        params.cns = valor.replace(/\D/g, '')
      }

      if (opcoes.nome) {
        params.nome = valor
      }

      if (opcoes.data && dataNascimento) {
        params.dt_nascimento = dataNascimento
      }

      if (Object.keys(params).length === 0) {
        this.loading = false
        this.$toast.error('Informe um critério de busca.', 'Erro:', {
          position: 'topRight',
        })
        return
      }

      const url = `/app/v2/cidadao/${this.$store.state.cod_ibge}`

      this.$api
        .get(url, { params })
        .then((response) => {
          this.resultados = response.data
        })
        .catch((error) => {
          console.log(error)
          this.$toast.error('Ocorreu um erro ao buscar.', 'Erro:', {
            position: 'topRight',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Limpa os resultados da busca de cidadãos.
     */
    limparFiltros() {
      this.resultados = []
    },

    exportarResultados() {},

    /**
     * Abre o modal para visualizar os dados do cidadão selecionado.
     * @param {Object} c Dados do cidadão
     * @param {String|Number} key Identificador da linha selecionada
     */
    async visualizarCidadao(c, key) {
      try {
        this.loadingCidKey = key

        this.pessoa = { ...c }

        await this.verificarPendenciasCriticasDoCidadao()

        this.$bvModal.show('modalCidadao')
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingCidKey = null
      }
    },

    /**
     * Verifica pendências críticas relacionadas ao cidadão.
     * Consulta duplicidades, cadastros e indicadores para identificar inconsistências.
     */
    async verificarPendenciasCriticasDoCidadao() {
      let pendencias_criticas = {
        pendencia_1: null,
        pendencia_2: null,
        pendencia_3: null,
        pendencia_4: null,
        pendencia_5: null,
        pendencia_6: null,
        pendencia_7: {
          active: false,
          text: '',
        },
        pendencia_8: [],
        pendencia_9: null,
      }

      if (this.fonteSincronizadorC.qtdDiasUltimaAtualizacao > 2) {
        pendencias_criticas.pendencia_1 = true
      }

      if (this.pessoa.saiu != 0 && this.pessoa.atend_pos_saiu == 1) {
        pendencias_criticas.pendencia_2 = true
      }

      if (this.pessoa.ponderacao_cadastro == 0) {
        pendencias_criticas.pendencia_3 = true
      }

      if (this.pessoa.duplicado == 2) {
        pendencias_criticas.pendencia_4 = true
      }

      if (this.pessoa.tp_equipe != '70' && this.pessoa.tp_equipe != '76') {
        pendencias_criticas.pendencia_5 = true
      }

      const response = await this.$api.post(`/app/cidadao/duplicados`, {
        co_ibge: this.$store.state.cod_ibge,
        id: this.pessoa.id,
        id_master: this.pessoa.id_master,
      })
      if (response.data.length > 1) {
        pendencias_criticas.pendencia_6 = true
      }

      const response2 = await this.$api.get(
        `/app/cidadao/${this.$store.state.cod_ibge}/${this.pessoa.nu_cpf}/${this.pessoa.nu_cns}/cadastros`
      )
      if (response2) {
        if (response2.data.length > 0) {
          // Pega a lista de CNS do cidadão
          this.cnsList = [
            ...new Set(
              response2.data
                .map((i) => (i?.cns != null ? String(i.cns).trim() : null))
                .filter((cns) => cns !== null && cns !== '')
            ),
          ]
          if (this.cnsList.length == 0) {
            this.cnsList = [0]
          }

          let dataMaxima = response2.data.reduce((max, obj) => {
            let [dia, mes, ano] = obj.dt_registro.split('/')
            let dataAtual = new Date(+ano, +mes - 1, +dia)
            return dataAtual > max ? dataAtual : max
          }, new Date(0))
          // Formatar para yyyy-mm-dd
          let dataMaximaFormatada = dataMaxima.toISOString().split('T')[0]
          if (
            new Date(dataMaximaFormatada) >
            new Date(this.pessoa.dt_ultima_ficha)
          ) {
            pendencias_criticas.pendencia_7.active = true
            pendencias_criticas.pendencia_7.text = `Cadastro Individual de ${
              this.pessoa.no_cidadao
            } do dia ${this.formatDate(
              dataMaximaFormatada
            )} é mais recente. Envie essa mensagem ao nosso suporte para ressincronizarmos esses cadastros.`
          }
          // A maior é sempre a primeira, devido o order by na query
          let maiorDtNascimento = response2.data[0].dt_nascimento
          if (maiorDtNascimento != this.pessoa.dt_nascimento) {
            pendencias_criticas.pendencia_9 = true
          }
        }
      }

      const response3 = await this.$api.post(
        `/app/cidadao/pendencias-indicadores`,
        {
          co_ibge: this.$store.state.cod_ibge,
          cfcp: this.pessoa.cfcp,
          idade: this.pessoa.idade,
        }
      )
      if (response3.data.length > 0) {
        pendencias_criticas.pendencia_8 = response3.data.map((el) => {
          let pendenciasFormatadas = JSON.parse(el.pendencias) ?? []
          return {
            ...el,
            pendencias: pendenciasFormatadas,
          }
        })
      }

      this.$set(this.pessoa, 'pendencias_criticas', pendencias_criticas)
    },
  },
}
</script>

<style scoped lang="scss"></style>
